/**
 * React-intl message catalog keys — BCP‑47 tags matching Smartling locale file names (`*.json`).
 * `whoami.locale` / `users.locale` may send full tags (e.g. `fr-FR`) or short codes (`fr`); aliases
 * resolve to the catalogs we ship.
 */
export const SUPPORTED_INTL_MESSAGE_LOCALES = [
  'en-US',
  'fr-FR',
  'de-DE',
  'it-IT',
  'es-ES',
] as const;

export type IntlMessageLocale = (typeof SUPPORTED_INTL_MESSAGE_LOCALES)[number];

export const DEFAULT_INTL_MESSAGE_LOCALE: IntlMessageLocale = 'en-US';

/**
 * Lowercase normalized locale strings (`-` separators) → shipped catalog key.
 * Includes short primary tags and common regional variants from the monolith / browsers.
 */
const LOCALE_ALIASES: Record<string, IntlMessageLocale> = {
  en: 'en-US',
  'en-us': 'en-US',
  /** No en-GB bundle; mirror Hire behavior by using American English catalog. */
  'en-gb': 'en-US',
  uk: 'en-US',

  es: 'es-ES',
  'es-es': 'es-ES',
  'es-mx': 'es-ES',
  'es-us': 'es-ES',
  'es-ar': 'es-ES',
  'es-co': 'es-ES',

  fr: 'fr-FR',
  'fr-fr': 'fr-FR',
  'fr-ca': 'fr-FR',
  'fr-be': 'fr-FR',
  'fr-ch': 'fr-FR',

  de: 'de-DE',
  'de-de': 'de-DE',
  'de-at': 'de-DE',
  'de-ch': 'de-DE',

  it: 'it-IT',
  'it-it': 'it-IT',
  'it-ch': 'it-IT',
};

function normalizeLocaleString(raw: string): string {
  return raw.trim().replace(/_/g, '-');
}

/** Maps a raw locale string to the catalog key we use in `translationMessages`. */
export function resolveIntlMessageLocale(
  raw: string | undefined,
  fallback: IntlMessageLocale,
): IntlMessageLocale {
  if (!raw) return fallback;

  const normalized = normalizeLocaleString(raw);
  const lower = normalized.toLowerCase();

  const exactKey = SUPPORTED_INTL_MESSAGE_LOCALES.find(
    k => k.toLowerCase() === lower,
  );
  if (exactKey) return exactKey;

  if (LOCALE_ALIASES[lower]) {
    return LOCALE_ALIASES[lower];
  }

  const primary = lower.split('-')[0] ?? lower;
  if (LOCALE_ALIASES[primary]) {
    return LOCALE_ALIASES[primary];
  }

  return fallback;
}
